import React, { forwardRef, useId } from 'react';

/** Label above control, hint or error below. Wires up ids and aria for you. */
export const FormField = ({
    label,
    htmlFor,
    required = false,
    hint,
    error,
    children,
    className = '',
}) => {
    const autoId = useId();
    const id = htmlFor || autoId;
    const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

    return (
        <div className={className}>
            {label && (
                <label htmlFor={id} className={`form-label ${required ? 'form-label-required' : ''}`}>
                    {label}
                </label>
            )}
            {typeof children === 'function'
                ? children({ id, 'aria-describedby': describedBy, 'aria-invalid': !!error })
                : React.isValidElement(children)
                    ? React.cloneElement(children, {
                        id: children.props.id || id,
                        'aria-describedby': children.props['aria-describedby'] || describedBy,
                        'aria-invalid': error ? true : children.props['aria-invalid'],
                    })
                    : children}
            {error ? (
                <p id={`${id}-error`} className="form-error" role="alert">
                    {error}
                </p>
            ) : (
                hint && (
                    <p id={`${id}-hint`} className="form-hint">
                        {hint}
                    </p>
                )
            )}
        </div>
    );
};

export const Input = forwardRef(function Input({ className = '', error, ...props }, ref) {
    return <input ref={ref} className={`input ${error ? 'input-error' : ''} ${className}`} {...props} />;
});

export const Textarea = forwardRef(function Textarea({ className = '', error, rows = 4, ...props }, ref) {
    return (
        <textarea
            ref={ref}
            rows={rows}
            className={`input textarea ${error ? 'input-error' : ''} ${className}`}
            {...props}
        />
    );
});

export const Select = forwardRef(function Select({ className = '', error, children, ...props }, ref) {
    return (
        <select ref={ref} className={`input ${error ? 'input-error' : ''} ${className}`} {...props}>
            {children}
        </select>
    );
});

/** Two-column form grid that collapses to one column below `sm`. */
export const FormGrid = ({ children, cols = 2, className = '' }) => (
    <div
        className={`grid gap-4 ${cols === 3 ? 'sm:grid-cols-3' : cols === 1 ? '' : 'sm:grid-cols-2'} ${className}`}
    >
        {children}
    </div>
);

export const FormSection = ({ title, description, children, className = '' }) => (
    <section className={`space-y-4 ${className}`}>
        {(title || description) && (
            <div>
                {title && <h3 className="text-md font-semibold text-ink-900">{title}</h3>}
                {description && <p className="text-label text-ink-500 mt-0.5">{description}</p>}
            </div>
        )}
        {children}
    </section>
);

export default FormField;
